/*
 * PROJECT-TASK-LIST.JSX - Stage Accordion Project Task List with Global User Permissions
 *
 * RECENT CHANGES - Permission Integration and Enhanced Task Management
 *
 * SUMMARY:
 * Project task list accordion component that renders project tasks within accordion stages
 * with enhanced permission controls for global users during critical project states.
 * Manages task display, drag-and-drop functionality, and section operations with permission validation.
 *
 * PROPS:
 * - isGlobalUser: Boolean for global user privilege validation
 * - inProgressQuote: Boolean for quote processing state
 * - updatePermission: Standard update permissions
 * - writePermission: Standard write permissions
 */
import React, { useState } from "react";
import idx from "idx";
import { roundValue } from "../../util/decimal-utils";
import { getProjectType } from "../../routes/projects/utils/getProjectStatus";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Tasks from "../../routes/projects/tasks";
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
  AccordionItem,
  Modal,
} from "react-bootstrap";
import { getFormatedData } from "../../routes/projects/task-list/task";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import { MenuItem, Menu, Tooltip } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { extractLastStageData } from "../../util/common";
import { useDispatch } from "react-redux";
import { warningSnackBar } from "../../actions/common.action";
import Icons from "../../components/icons/icons";
import { SectionMenu } from "./../../util/enums";
import Button from "react-bootstrap/Button";
import { getActualHoursColorClass } from "../../routes/projects/utils/hours-calculation";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

function StageAccordionTaskList({
  index,
  stagesLength,
  stage,
  projectSummary,
  handleStageClick,
  handleTaskClick,
  handleTaskTemplateDialog,
  optionDialogHandler,
  deleteDialogHandler,
  handleOptionMenu,
  taskListState,
  writePermission,
  financePermission,
  updatePermission,
  _editProject,
  onDragEnd,
  switchSection,
  copyTasksToSection,
  stageTotals,
  optionsTotal,
  isConvertedToCurrentProject,
  dragHandleProps,
  isDragging,
  onDragEndSections,
  projectId,
  sectionHeader = [],
  isGlobalUser,
  handleToggleAttributionLevel,
  onRequestStageFieldOpsToggle,
  onRequestTaskFieldOpsToggle,
  onRequestVariationFieldOpsToggle,
  parentIsAttributed = false,
  parentIsFieldOpsExcluded = false,
}) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [showAttributionModal, setShowAttributionModal] = useState(false);

  let { isQuote, isVariation, isCurrent } = getProjectType({
    type: idx(projectSummary, (_) => _.project.res.type),
    status: idx(projectSummary, (_) => _.project.res.status),
    projectQuoteType: idx(projectSummary, (_) => _.project.res.quoteType),
  });

  let projectType = idx(projectSummary, (_) => _.project.projectType);
  const projectStatus = idx(projectSummary, (_) => _.project.res.status);
  const quoteType = idx(projectSummary, (_) => _.project.quoteType);
  const inProgressQuote =
    projectStatus == "IN_PROGRESS" && quoteType === "quote";
  const hasNestedStages =
    Array.isArray(stage.stages) && stage.stages.length > 0;
  const isFieldOpsInheritedExcluded =
    parentIsFieldOpsExcluded || Boolean(stage.exclude_from_field_ops);
  const isStageFieldOpsDirectlyExcluded = Boolean(stage.exclude_from_field_ops);
  const canToggleStageFieldOps = updatePermission && !parentIsFieldOpsExcluded;
  const stageFieldOpsTooltip = parentIsFieldOpsExcluded
    ? "Excluded from field ops via parent section"
    : isStageFieldOpsDirectlyExcluded
      ? "Excluded from field ops"
      : "Included in field ops";
  const stageId = idx(stage, (_) => _.id) || null;
  const parentId = idx(stage, (_) => _.parent_stage_id) || null;
  const nestedStageId = idx(stage, (_) => _.stages[0].id) || null;
  const lockIcon = ["IN_PROGRESS", "NOT_STARTED"];
  const showLockIcon = lockIcon.includes(projectStatus);

  const {
    materialSaleAmount,
    laborSaleAmount,
    subcontractorSaleAmount,
    totalSaleAmount,
    materialProfit,
    laborProfit,
    subcontractorProfit,
    totalProfit,
    materialProfitPercentage,
    laborProfitPercentage,
    subcontractorProfitPercentage,
    totalProfitPercentage,
    completionPercentage,
    effortCompletionPercentage,
  } = getFormatedData(
    hasNestedStages
      ? idx(stageTotals, (_) => _[stage.id]) || {}
      : idx(stageTotals, (_) => _[stage.id].taskList.total) || {}
  );

  const [sectionMenu, setSectionMenu] = useState({
    anchorEl: null,
    currentEditType: "",
    currentEditId: {},
    sectionItems: {},
  });

  const handleSectionMenu = (event, type, sectionId, stage) => {
    event.stopPropagation();
    let menuItem = {},
      sectionItem = {};

    if (type === SectionMenu.TYPE) {
      const hasTasks = Array.isArray(stage.tasks) && stage.tasks.length > 0;
      const hasSubsections =
        Array.isArray(stage.stages) && stage.stages.length > 0;
      if (!hasTasks || hasSubsections) {
        sectionItem.sub = SectionMenu.ADDSUBSECTION;
      }
      sectionItem.edit = stage.parent_stage_id
        ? SectionMenu.EDITSUBSECTION
        : SectionMenu.EDIT;
      sectionItem.delete = stage.parent_stage_id
        ? SectionMenu.DELETESUBSECTION
        : SectionMenu.DELETE;
    }

    setSectionMenu((prev) => ({
      ...prev,
      anchorEl: event.currentTarget,
      menuItems: menuItem,
      sectionItems: sectionItem,
      currentEditType: type,
      currentEditId: { sectionId },
    }));
  };

  const handleMenuClose = (event) => {
    event.stopPropagation();
    setSectionMenu((prev) => ({
      ...prev,
      anchorEl: null,
      currentEditType: "",
      currentEditId: {},
    }));
  };

  const sectionMenuHandler = (event, data) => {
    handleMenuClose(event);
    const lastStageData = extractLastStageData({ stages: [data.stage] });
    const isHavingTasks =
      lastStageData.length &&
      lastStageData.some(
        (stage) =>
          idx(stage, (_) => _.tasks) && idx(stage, (_) => _.tasks).length,
      );
    if (isHavingTasks && data.deleteSection) {
      dispatch(warningSnackBar("Please move all the Tasks to other Section"));
      return;
    }
    if (data.deleteSection) {
      const type = lastStageData[0].parent_stage_id ? "Sub-Section" : "Section";
      deleteDialogHandler(data.stageId, type, data.stage.name);
    } else {
      handleStageClick(
        data.stageId,
        data.parentId,
        data.nestedStageId,
        data.addSection,
        data.stage,
      );
    }
  };

  const onDragEndNested = async (result) => {
    onDragEndSections(result, Array.from(stage.stages));
  };

  const handleToggle = () => {
    handleToggleAttributionLevel(
      stage.id,
      !stage.is_attribution_level,
      projectId,
    );
  };

  // Add these lines to determine project state for attribution logic
  const isQuoteProject = isQuote || projectType === "TENDER";
  const isArchivedProject =
    projectStatus && ["LOST", "CANCELLED", "ARCHIVED"].includes(projectStatus);
  const isDraftProject = projectStatus === "NOT_STARTED";
  const isPendingProject = projectStatus === "DLP";

  // Attribution should be editable for: quotes/tenders, draft, pending, archived projects
  const allowAttributionToggle =
    isQuoteProject || isDraftProject || isPendingProject || isArchivedProject;

  return (
    <Accordion
      key={stage.id}
      activeKey={open ? stage.id : null}
      onSelect={() => setOpen(!open)}
      className={`${stage.parent_stage_id ? "p-0 nested-section" : ""} ${isDragging ? "dragging" : ""}`}
    >
      {index === 0 && (
        <ul
          className="accordion-icon mb-0 stage-total-row tabel-list d-flex section-header-label justify-content-sm-between list-unstyled"
          data-cy={"section-header-label"}
        >
          <span className="section-header-spacer" aria-hidden="true" />
          <li className="first-cell text-start me-auto tender-task-total w-25">
            <span></span>
          </li>
          {projectType !== "TENDER" ? (
            <>
              {financePermission && (
                <>
                  <li
                    className="d-flex flex-column justify-content-end align-items-end tender-task-total text-end w-150px"
                    data-cy="material"
                  >
                    <span className="fw-bold text-uppercase wbs-header-title">Material Sale</span>
                    <span className="wbs-header-subtitle">Profit (%)</span>
                  </li>
                  <li
                    className="d-flex flex-column justify-content-end align-items-end tender-task-total text-end w-150px"
                    data-cy="contractor"
                  >
                    <span className="fw-bold text-uppercase wbs-header-title">Contractor Sale</span>
                    <span className="wbs-header-subtitle">Profit (%)</span>
                  </li>
                  <li
                    className="d-flex flex-column justify-content-end align-items-end tender-task-total text-end w-150px"
                    data-cy="resource"
                  >
                    <span className="fw-bold text-uppercase wbs-header-title">Resource Sale</span>
                    <span className="wbs-header-subtitle">Profit (%)</span>
                  </li>
                </>
              )}
              <li
                className="d-flex flex-column justify-content-end align-items-end tender-task-total text-end w-150px"
                data-cy="effort"
              >
                <span className="fw-bold text-uppercase wbs-header-title">Effort Hours</span>
                <span className="wbs-header-subtitle">Actual (%)</span>
              </li>
              <li className="align-self-end tender-task-total text-end w-150px">
                <span className="fw-bold text-uppercase wbs-header-title">Completion %</span>
              </li>
            </>
          ) : (
            <>
              <li
                className="d-flex flex-column justify-content-end align-items-end tender-task-total text-end w-150px"
                data-cy="planned-effort"
              >
                <span className="fw-bold text-uppercase wbs-header-title">Planned Effort</span>
              </li>
              <li
                className="d-flex flex-column justify-content-end align-items-end tender-task-total text-end w-150px"
                data-cy="material"
              >
                <span className="fw-bold text-uppercase wbs-header-title">Material</span>
                <span className="wbs-header-subtitle">Profit (%)</span>
              </li>
              <li
                className="d-flex flex-column justify-content-end align-items-end tender-task-total text-end w-150px"
                data-cy="contractor"
              >
                <span className="fw-bold text-uppercase wbs-header-title">Contractor</span>
                <span className="wbs-header-subtitle">Profit (%)</span>
              </li>
              <li
                className="d-flex flex-column justify-content-end align-items-end tender-task-total text-end w-150px"
                data-cy="resource"
              >
                <span className="fw-bold text-uppercase wbs-header-title">Resource</span>
                <span className="wbs-header-subtitle">Profit (%)</span>
              </li>
            </>
          )}
          <li
            className="d-flex flex-column justify-content-center align-items-end tender-task-total text-end w-180px"
            data-cy="sales-total-amount"
          >
            <span className="fw-bold text-uppercase wbs-header-title">Total Sale</span>
            <span className="wbs-header-subtitle">Profit (%)</span>
          </li>
          {/* {showLockIcon ? (<span className="lock align-self-center">Lock<br />Section</span>) : (<span className="report">Report<br />Flag</span>)} */}
          <li
            className={`d-flex flex-column justify-content-center align-items-end text-center wbs-lock-label 
          ${showLockIcon ? "lock" : "wbs-mapping"}`}
          ></li>
          <li
            className={`text-center tender-task-total ${showLockIcon ? "lock" : "wbs-mapping"}`}
          ></li>
          <li
            className={`text-center tender-task-total ${showLockIcon ? "lock" : "wbs-mapping"}`}
          ></li>
          {/* <li className="text-end tender-task-total w-5"></li> */}
        </ul>
      )}
      <AccordionItem eventKey={stage.id}>
        <AccordionHeader
          className={`accordion-icon border projectableContainer justify-content-between tabel-list ${stage.is_attribution_level ? "locked-section" : ""} ${stage.parent_stage_id && inProgressQuote ? "nested-stage-header" : "nested-stage-header-quote"}`}
          eventKey={stage.id}
        >
          {!stage.parent_stage_id && (
            <span {...dragHandleProps} className="cursor-grab w-2">
              <Icons icon="dragHandle" />
            </span>
          )}
          <li className="list-group-item acc-header-text text-truncate me-auto w-23">
            {stage.name}
          </li>

          {projectType !== "TENDER" && (
            <>
              {financePermission && (
                <>
                  <li
                    className="text-end list-group-item total-color d-flex flex-column w-150px"
                    data-cy="material"
                  >
                    <span data-cy="sale-amount">{materialSaleAmount}</span>
                    <h6 className="mb-0 profit-percent">
                      {materialProfit} ({materialProfitPercentage})
                    </h6>
                  </li>
                  <li
                    className="text-end list-group-item total-color d-flex flex-column w-150px"
                    data-cy="contractor"
                  >
                    <span data-cy="sale-amount">{subcontractorSaleAmount}</span>
                    <h6 className="mb-0 profit-percent">
                      {subcontractorProfit} ({subcontractorProfitPercentage})
                    </h6>
                  </li>
                  <li
                    className="text-end list-group-item total-color d-flex flex-column w-150px"
                    data-cy="resource"
                  >
                    <span data-cy="sale-amount">{laborSaleAmount}</span>
                    <h6 className="mb-0 profit-percent">
                      {laborProfit} ({laborProfitPercentage})
                    </h6>
                  </li>
                </>
              )}
              <li className="text-end list-group-item total-color d-flex flex-column w-150px">
                <span data-cy="section-planned-hours">
                  {stage.forecastHoursDisplay || "0 hrs"}
                </span>
                {!parentIsAttributed ? (
                  <h6 className="mb-0 profit-percent" data-cy="section-actual-hours">
                    {stage.actualHoursDisplay || "0 hrs"} (
                    <span>{stage.effortCompletionPercentage}</span>)
                  </h6>
                ) : (
                  <span className="text-muted">-</span>
                )}
              </li>
            </>
          )}
          {projectType === "TENDER" && (
            <>
              <li className="list-group-item total-color text-end w-180px d-flex flex-column justify-content-center align-items-end">
                <span data-cy="section-planned-hours">
                  {stage.forecastHoursDisplay || "0 hrs"}
                </span>
              </li>
              <li
                className="text-end list-group-item total-color d-flex flex-column w-150px"
                data-cy="material"
              >
                <span data-cy="sale-amount">{materialSaleAmount}</span>
                <h6 className="mb-0 profit-percent">
                  {materialProfit} ({materialProfitPercentage})
                </h6>
              </li>
              <li
                className="text-end list-group-item total-color d-flex flex-column w-150px ms-1"
                data-cy="contractor"
              >
                <span data-cy="sale-amount">{subcontractorSaleAmount}</span>
                <h6 className="mb-0 profit-percent">
                  {subcontractorProfit} ({subcontractorProfitPercentage})
                </h6>
              </li>
              <li
                className="text-end list-group-item total-color d-flex flex-column w-150px"
                data-cy="resource"
              >
                <span data-cy="sale-amount">{laborSaleAmount}</span>
                <h6 className="mb-0 profit-percent">
                  {laborProfit} ({laborProfitPercentage})
                </h6>
              </li>
            </>
          )}
          {projectType !== "TENDER" && (
            <li className="list-group-item total-color text-end w-150px">
              <div data-cy="section-completion">
                {parentIsAttributed ? (
                  <span className="text-muted">-</span>
                ) : (
                  `${completionPercentage}`
                )}
              </div>
            </li>
          )}
          <li
            className={`text-right list-group-item total-color d-flex flex-column w-180px`}
            data-cy="total"
          >
            <span data-cy="sale-amount">{totalSaleAmount}</span>
            <h6 className="mb-0 profit-percent">
              {totalProfit} ({totalProfitPercentage})
            </h6>
          </li>
          <span
            className="d-flex wbs-lock-icon align-items-center"
            data-cy={"attribution-level-icon"}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              // For active projects (isCurrent): use original locked logic
              // For other project states: allow toggling
              if (
                (isCurrent
                  ? !stage.is_attribution_level
                  : allowAttributionToggle || !stage.is_attribution_level) &&
                !parentIsAttributed
                // Uncomment to allow global users to override attribution restrictions in development environment
                // && (isGlobalUser && process.env.NODE_ENV === 'development')
              ) {
                // For quotes, show modal only once per page load using sessionStorage
                if (isQuote) {
                  const modalShown =
                    sessionStorage.getItem("quoteModalShown") === "true";
                  if (!modalShown) {
                    setShowAttributionModal(true);
                  } else {
                    // Modal already shown, just toggle directly
                    handleToggle();
                  }
                } else {
                  // For non-quotes, always show modal
                  setShowAttributionModal(true);
                }
              }
            }}
          >
            {!parentIsAttributed && (
              <>
                {/* {!stage.parent_stage_id && <span className="wbs-lock-icon-placeholder"></span>} */}
                {stage.is_attribution_level ? (
                  // For active projects (isCurrent): show lock icon
                  // For editable project states: show checkbox to allow toggling off
                  isCurrent ? (
                    <Tooltip title="Attributed Section" arrow>
                      <AssignmentTurnedInIcon
                        className="attribution-lock-icon"
                        color="primary"
                      />
                    </Tooltip>
                  ) : (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        // For quotes, toggle directly since modal is temporarily disabled
                        if (isQuote) {
                          handleToggle();
                        } else {
                          // For non-quotes, always show modal (when re-enabled)
                          setShowAttributionModal(true);
                        }
                      }}
                      style={{ display: "inline-block" }}
                    >
                      <Checkbox
                        key="attribution-checkbox-disable"
                        size="small"
                        checked={stage.is_attribution_level || false}
                        className="p-0 wbs-lock-checkbox"
                        title="Disable Attribution Level"
                      />
                    </div>
                  )
                ) : // Show checkbox to enable attribution
                  // Feature Flag: Remove isQuote when resolved CTBL-513
                  isQuote ? (
                    <div
                      style={{ marginRight: "8px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        // For quotes, toggle directly since modal is temporarily disabled
                        if (isQuote) {
                          handleToggle();
                        } else {
                          // For non-quotes, always show modal (when re-enabled)
                          setShowAttributionModal(true);
                        }
                      }}
                    >
                      <Checkbox
                        key="attribution-checkbox-enable"
                        size="small"
                        checked={stage.is_attribution_level || false}
                        className="p-0 wbs-lock-checkbox"
                        title={
                          stage.is_attribution_level
                            ? "Disable Attribution Level"
                            : "Enable Attribution Level"
                        }
                      />
                    </div>
                  ) : (
                    // Feature Flag: Remove this div temporary holder when resolved CTBL-513
                    <div style={{ width: "24px", height: "24px" }}></div>
                  )}
              </>
            )}
          </span>
          {parentIsAttributed && (
            <>
              <div className="wbs-lock-icon-placeholder"></div>
            </>
          )}
          {!parentIsAttributed && (
            <span
              className="ms-2"
              data-cy={"exclude-field-ops-icon"}
              onClick={
                canToggleStageFieldOps
                  ? (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onRequestStageFieldOpsToggle(
                      stage.id,
                      !isStageFieldOpsDirectlyExcluded,
                      stage.name,
                    );
                  }
                  : undefined
              }
            >
              <Tooltip title={stageFieldOpsTooltip} arrow>
                {isFieldOpsInheritedExcluded ? (
                  <VisibilityOffIcon
                    className="attribution-lock-icon"
                    sx={{ color: "var(--brand-color-2)" }}
                  />
                ) : (
                  <VisibilityIcon
                    className="attribution-lock-icon"
                    sx={{ color: "var(--brand-color-2)" }}
                  />
                )}
              </Tooltip>
            </span>
          )}
          <span
            onClick={(e) => {
              e.stopPropagation();
              handleSectionMenu(e, SectionMenu.TYPE, stageId, stage);
            }}
            className={`menuIcon ${inProgressQuote ? "pe-none" : "show"}`}
            data-cy="section-menu"
          >
            <MoreHorizOutlinedIcon
              className={`${inProgressQuote ? "invisible" : "visible"}`}
            />
          </span>
          <Menu
            id="simple-menu"
            anchorEl={sectionMenu.anchorEl}
            keepMounted
            open={Boolean(sectionMenu.anchorEl)}
            onClose={handleMenuClose}
          >
            {Object.entries(sectionMenu.sectionItems).map(
              ([key, value], index) => {
                let data = {};
                if (
                  value === SectionMenu.EDIT ||
                  value === SectionMenu.EDITSUBSECTION
                )
                  data = {
                    stageId,
                    parentId,
                    nestedStageId,
                    addSection: false,
                    stage,
                    deleteSection: false,
                  };
                else if (value === SectionMenu.ADDSUBSECTION)
                  data = {
                    stageId,
                    parentId: null,
                    nestedStageId: null,
                    addSection: true,
                    stage,
                    deleteSection: false,
                  };
                else if (
                  value === SectionMenu.DELETE ||
                  value === SectionMenu.DELETESUBSECTION
                )
                  data = {
                    stageId,
                    parentId,
                    nestedStageId,
                    addSection: true,
                    stage,
                    deleteSection: true,
                  };
                return (
                  <MenuItem
                    key={index}
                    disabled={!updatePermission}
                    onClick={(e) => sectionMenuHandler(e, data)}
                    data-cy={
                      value === SectionMenu.EDIT
                        ? "sectionmenu-edit"
                        : value === SectionMenu.EDITSUBSECTION
                          ? "sectionmenu-editsubsection"
                          : value === SectionMenu.ADD
                            ? "sectionmenu-add"
                            : value === SectionMenu.ADDSUBSECTION
                              ? "sectionmenu-addsubsection"
                              : value === SectionMenu.DELETE
                                ? "sectionmenu-delete"
                                : value === SectionMenu.DELETESUBSECTION
                                  ? "sectionmenu-deletesubsection"
                                  : "sectionmenu-unknown"
                    }
                  >
                    {value}
                  </MenuItem>
                );
              },
            )}
          </Menu>
        </AccordionHeader>
        <AccordionBody
          className={`${!hasNestedStages ? "p-0" : stage.stages ? "p-0 ps-2" : ""}`}
        >
          <div style={{ width: "100%", overflowX: "auto" }}>
            {" "}
            {/* Ensure horizontal scroll if content overflows */}
            {hasNestedStages ? (
              <DragDropContext onDragEnd={onDragEndNested}>
                <Droppable droppableId={`nested-${stage.id}`}>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {[...stage.stages]
                        .sort((a, b) => a.ordering_id - b.ordering_id)
                        .map((nestedStage, index) => (
                          <Draggable
                            key={nestedStage.id}
                            draggableId={nestedStage.id}
                            index={index}
                            isDragDisabled={!updatePermission}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  marginBottom: "8px",
                                }}
                                className="sub-cursor"
                              >
                                <span
                                  {...provided.dragHandleProps}
                                  className="cursor-grab sub-cursor-grab"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Icons icon="dragHandle" />
                                </span>
                                <StageAccordionTaskList
                                  key={`${nestedStage.id}`}
                                  stage={nestedStage}
                                  {...{
                                    stagesLength,
                                    projectSummary,
                                    handleStageClick,
                                    handleTaskClick,
                                    handleTaskTemplateDialog,
                                    optionDialogHandler,
                                    deleteDialogHandler,
                                    updatePermission,
                                    handleOptionMenu,
                                    taskListState,
                                    writePermission,
                                    financePermission,
                                    _editProject,
                                    onDragEnd,
                                    materialSaleAmount,
                                    laborSaleAmount,
                                    subcontractorSaleAmount,
                                    totalSaleAmount,
                                    switchSection,
                                    copyTasksToSection,
                                    stageTotals,
                                    optionsTotal,
                                    isConvertedToCurrentProject,
                                    dragHandleProps,
                                    projectId,
                                    onDragEndSections,
                                    isDragging: snapshot.isDragging,
                                    sectionHeader,
                                    isGlobalUser,
                                    handleToggleAttributionLevel,
                                    onRequestStageFieldOpsToggle,
                                    onRequestTaskFieldOpsToggle,
                                    onRequestVariationFieldOpsToggle,
                                    parentIsAttributed:
                                      stage.is_attribution_level ||
                                      parentIsAttributed,
                                    parentIsFieldOpsExcluded:
                                      stage.exclude_from_field_ops ||
                                      parentIsFieldOpsExcluded,
                                  }}
                                />
                              </div>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              <div className="viewProjectCol task-table-aligned">
                <div className="projectableContainer">
                  <div className="border-0 p-0 ps-2 table-top-row">
                    <div className="col-12 col-sm-2">
                      <h6 className="m-1">Tasks</h6>{" "}
                      {/* Common label aligned with values */}
                    </div>
                    <div className="col-12 col-sm-10 buttons">
                      {writePermission && stage && (
                        <div
                          className="task-buttons ms-auto btn-task d-flex me-3 gap-2 align-items-center"
                          key={stage.id}
                        >
                          {(isQuote || !isVariation || !inProgressQuote) && (
                            <>
                              <Button
                                type="submit"
                                className="ms-auto task-save-btn secondarybtn mb-2"
                                data-cy={"task-template-btn"}
                                onClick={() =>
                                  handleTaskTemplateDialog(stage.id)
                                }
                              >
                                TASK TEMPLATES
                              </Button>
                              <Button
                                type="submit"
                                className="ms-auto add-task-btn secondarybtn mb-2"
                                data-cy={"add-task-btn"}
                                onClick={() =>
                                  handleTaskClick({ stageId: stage.id })
                                }
                              >
                                ADD TASK
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="privewTable">
                    <div className="table-responsive">
                      <DragDropContext onDragEnd={(e) => onDragEnd(e, stage)}>
                        <Droppable droppableId="tasks">
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                            >
                              <ul className="tabel-list table-head">
                                <li className="first-cell w-25">
                                  <span className="task-table-drag-placeholder" />
                                  Name
                                </li>
                                {idx(
                                  projectSummary,
                                  (_) => _.project.projectType,
                                ) !== "TENDER" && (
                                    <>
                                      {financePermission && (
                                        <>
                                          <li className="my-auto w-150px text-end section-header-label">
                                            Material
                                          </li>
                                          <li className="my-auto w-150px text-end section-header-label">
                                            Contractor
                                          </li>
                                          <li className="my-auto w-150px text-end section-header-label">
                                            Resource
                                          </li>
                                        </>
                                      )}
                                      <li className="my-auto w-150px text-end section-header-label">
                                        Effort
                                      </li>
                                    </>
                                  )}
                                {idx(
                                  projectSummary,
                                  (_) => _.project.projectType,
                                ) !== "TENDER" && (
                                    <li className="my-auto w-150px text-end section-header-label">
                                      Completion %
                                    </li>
                                  )}
                                {financePermission &&
                                  idx(
                                    projectSummary,
                                    (_) => _.project.projectType,
                                  ) === "TENDER" && (
                                    <>
                                      <li className="my-auto text-end w-150px section-header-label">
                                        Planned Effort
                                      </li>
                                      <li className="my-auto text-end w-150px section-header-label">
                                        Material
                                      </li>
                                      <li className="my-auto text-end w-150px section-header-label">
                                        Contractor
                                      </li>
                                      <li className="my-auto text-end w-150px section-header-label">
                                        Resource
                                      </li>
                                    </>
                                  )}
                                {financePermission && (
                                  <li className="my-auto text-end w-180px flex flex-col section-header-label">
                                    <span>Sales</span>
                                    <br />
                                    <span>Profit (%)</span>
                                  </li>
                                )}
                                {financePermission &&
                                  !parentIsAttributed &&
                                  !stage.is_attribution_level ? (
                                  <li className="my-auto action-icons-task pe-3 text-end w-5 section-header-label">
                                    Actions
                                  </li>
                                ) : (
                                  <li className="my-auto action-icons-task pe-3 text-end w-5">
                                    <div style={{ width: "60px" }}></div>
                                  </li>
                                )}
                              </ul>

                              {stage && (
                                <Tasks
                                  parentIsAttributed={parentIsAttributed}
                                  stages={[stage] || []}
                                  deleteDialogHandler={deleteDialogHandler}
                                  switchSection={switchSection}
                                  copyTasksToSection={copyTasksToSection}
                                  handleTaskClick={handleTaskClick}
                                  stagesCost={
                                    idx(
                                      stageTotals,
                                      (_) => _[stage.id].taskList.total,
                                    ) || {}
                                  }
                                  taskCost={
                                    idx(
                                      stageTotals,
                                      (_) => _[stage.id].taskList.tasks,
                                    ) || {}
                                  }
                                  projectType={idx(
                                    projectSummary,
                                    (_) => _.project.projectType,
                                  )}
                                  financePermission={financePermission}
                                  updatePermission={updatePermission}
                                  writePermission={writePermission}
                                  quoteType={idx(
                                    projectSummary,
                                    (_) => _.project.quoteType,
                                  )}
                                  toggleId={taskListState.toggleId}
                                  stageId={stage.id || ""}
                                  stagesLength={stagesLength}
                                  inProgressQuote={inProgressQuote}
                                  isGlobalUser={isGlobalUser}
                                  isSectionLocked={
                                    stage.is_attribution_level ||
                                    parentIsAttributed
                                  }
                                  canInteractWithTaskFieldOps={
                                    updatePermission || isGlobalUser
                                  }
                                  isFieldOpsInheritedExcluded={
                                    isFieldOpsInheritedExcluded
                                  }
                                  onRequestTaskFieldOpsToggle={
                                    onRequestTaskFieldOpsToggle
                                  }
                                />
                              )}

                              <Tasks
                                variations={
                                  idx(projectSummary, (_) => _.variationData) ||
                                  []
                                }
                                handleTaskClick={handleTaskClick}
                                deleteDialogHandler={deleteDialogHandler}
                                switchSection={switchSection}
                                copyTasksToSection={copyTasksToSection}
                                optionDialogHandler={optionDialogHandler}
                                handleOptionMenu={handleOptionMenu}
                                writePermission={writePermission}
                                projectType={idx(
                                  projectSummary,
                                  (_) => _.project.projectType,
                                )}
                                financePermission={financePermission}
                                updatePermission={updatePermission}
                                quoteType={idx(
                                  projectSummary,
                                  (_) => _.project.quoteType,
                                )}
                                optionsTotal={optionsTotal || {}}
                                toggleId={taskListState.toggleId}
                                stageId={stage.id || ""}
                                stagesLength={stagesLength}
                                inProgressQuote={inProgressQuote}
                                isGlobalUser={isGlobalUser}
                                isSectionLocked={stage.is_attribution_level}
                                canInteractWithTaskFieldOps={false}
                                isFieldOpsInheritedExcluded={
                                  isFieldOpsInheritedExcluded
                                }
                                onRequestVariationFieldOpsToggle={
                                  onRequestVariationFieldOpsToggle
                                }
                              />
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </AccordionBody>
      </AccordionItem>


      {showAttributionModal && (
        <Modal show={true} onHide={() => setShowAttributionModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Cost Attribution</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              You are{" "}
              <strong>flagging this section for cost attribution</strong>. This
              will update this Project to set cost attribution and reporting to
              this Section and not any child Sections or Tasks.
            </p>
            <p>
              {isCurrent ? (
                <>
                  As this is an active project, any existing costs associated
                  with child Sections and Tasks will be updated to be attributed
                  to this Section level.
                </>
              ) : (
                <>
                  Once this project becomes active, any future costs will be
                  attributed to this Section level rather than to child Sections
                  and Tasks.
                  <br />
                  <br />
                  <strong style={{ textDecoration: "underline" }}>
                    This attribution setting will become permanent and cannot be
                    reversed once the project is active.
                  </strong>
                </>
              )}
            </p>

            {isCurrent && (
              <p>
                Once this is confirmed you will{" "}
                <strong>not be able to reverse this. </strong>
              </p>
            )}

            {projectStatus === "IN_PROGRESS" && (
              <>
                {" "}
                <div>
                  <p>
                    <strong>What will happen once you proceed:</strong>
                  </p>
                </div>
                <ul>
                  <li>
                    <strong>Data Migration:</strong> All attributed cost from
                    child Sections and Tasks will be updated to report at this
                    level
                  </li>
                  <br />
                  <li>
                    <strong>Visibility Change:</strong> There will no longer be
                    any Actual cost reported at child Sections or Tasks
                  </li>
                  <br />
                  <li>
                    <strong>Attribution Impact:</strong> You will not be able to
                    attribute costs going forward to any child Sections or
                    Tasks, such as in Purchase Orders, Work Orders, and
                    Timesheets
                  </li>
                  <br />
                  <li>
                    <strong>Invoicing:</strong> Progress Claims and Completion
                    reporting will be done at this level
                  </li>
                </ul>
              </>
            )}
            <div className="alert alert-danger">
              <strong>
                Please confirm that you understand and would like to proceed.
              </strong>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowAttributionModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                handleToggle();
                setShowAttributionModal(false);
                // Set sessionStorage flag only when user proceeds
                if (isQuote) {
                  sessionStorage.setItem("quoteModalShown", "true");
                }
              }}
            >
              I Understand - Proceed
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Accordion>
  );
}

export default StageAccordionTaskList;
